import { parseISO, subDays } from 'date-fns';
import { normalizeAffiliationTypes } from '../../../utils/affiliationTypes';
import { SearchState, SearchAction, FilterState } from './types';

const sortingFieldNamesBack: Record<string, string> = {
  'published_at': 'DATE',
  'source': 'SOURCE',
  'message': 'MESSAGE',
  'views': 'VIEWS',
  'engagement': 'ENGAGEMENT',
  'relevance': 'RELEVANCE',
};

export const getEmptyFilterState = (): FilterState => ({
  platforms: { value: [], exclude: false },
  countries: { value: [], exclude: false },
  sourceGroups: { value: [], exclude: false },
  languages: { value: [], exclude: false },
  sources: { value: [], exclude: false },
  sentiment: { value: [], exclude: false },
  contentTypes: { value: [], exclude: false },
  bots: { value: null, exclude: false },
  discreditations: { value: [], exclude: false },
  affiliations: { value: [], exclude: false },
  affiliationTypes: { value: [], exclude: false },
  feeds: { value: [], exclude: false },
  geoGroup: { value: [], exclude: false },
  tags: { value: [], exclude: false },
});

export const getFiltersFromSearchParams = (searchParams: URLSearchParams): FilterState => ({
  platforms: {
    value: searchParams.getAll('platforms').map(i => parseInt(i)) || [],
    exclude: false
  },
  countries: {
    value: searchParams.getAll('source_origin_country_ids') || [],
    exclude: searchParams.get('countries_exclude') !== null
  },
  languages: {
    value: searchParams.getAll('languages').map(lang => parseInt(lang)) || [],
    exclude: searchParams.get('languages_exclude') !== null
  },
  sourceGroups: {
    value: searchParams.getAll('source_group_ids').map(i => parseInt(i)) || [],
    exclude: searchParams.get('groups_exclude') !== null
  },
  sources: {
    value: searchParams.getAll('source_ids').map(i => parseInt(i)) || [],
    exclude: searchParams.get('sources_exclude') !== null
  },
  sentiment: {
    value: searchParams.getAll('sentiment') || [],
    exclude: false
  },
  contentTypes: {
    value: searchParams.getAll('content_types') || [],
    exclude: false
  },
  bots: {
    value: searchParams.get('show_bots') === 'true' ? true : null,
    exclude: searchParams.get('bots_exclude') !== null
  },
  discreditations: {
    value: searchParams.getAll('IoC') || [],
    exclude: searchParams.get('discreditations_exclude') !== null
  },
  affiliations: {
    value: searchParams.getAll('affiliations') || [],
    exclude: searchParams.get('affiliations_exclude') !== null
  },
  affiliationTypes: {
    value: normalizeAffiliationTypes(searchParams.getAll('affiliation_types')),
    exclude: searchParams.get('affiliation_types_exclude') !== null
  },
  feeds: {
    value: searchParams.getAll('feeds').map(i => parseInt(i)) || [],
    exclude: searchParams.get('feeds_exclude') !== null
  },
  geoGroup: {
    value: searchParams.getAll('geo_group').map(i => parseInt(i)) || [],
    exclude: searchParams.get('geo_group_exclude') !== null
  },
  tags: {
    value: searchParams.getAll('tags') || [],
    exclude: searchParams.get('tags_exclude') !== null
  },
});

export const getInitialState = (
  searchParams: URLSearchParams,
  sortingParam: string = '-published_at'
): SearchState => {
  const similarTo = searchParams.get('similar_to');
  const commentsTo = searchParams.get('comments_to');
  const repostsTo = searchParams.get('reposts_to');

  let urlSorting = sortingParam;
  let defineAscending = false;

  if (urlSorting.startsWith('-')) {
    defineAscending = false;
    urlSorting = urlSorting.slice(1);
  } else {
    defineAscending = true;
  }

  const filters = getFiltersFromSearchParams(searchParams);

  return {
    parentCompositeId: commentsTo || repostsTo,
    isRepostsMode: !!repostsTo,
    sorting: { isAscending: defineAscending, fieldName: sortingFieldNamesBack[urlSorting] || 'DATE' },
    startDate: searchParams.get('start_date')
      ? parseISO(searchParams.get('start_date')!)
      : similarTo || commentsTo || repostsTo
        ? null
        : subDays(new Date(), 7),
    endDate: searchParams.get('end_date')
      ? parseISO(searchParams.get('end_date')!)
      : similarTo || commentsTo || repostsTo
        ? null
        : new Date(),
    examples: null,
    cursor: null,
    filters,
    draftFilters: { ...filters }, // Initialize draft as a copy of applied filters
  };
};

export const searchReducer = (state: SearchState, action: SearchAction): SearchState => {
  switch (action.type) {
    case 'APPLY_FILTER': {
      const { cursor, ...filterPayload } = action.payload;
      return {
        ...state,
        filters: {
          ...state.filters,
          ...filterPayload,
        },
        cursor: cursor !== undefined ? cursor : state.cursor,
      };
    }

    case 'UPDATE_LANGUAGES': {
      return {
        ...state,
        filters: {
          ...state.filters,
          languages: {
            ...state.filters.languages,
            value: action.payload,
          },
        },
      };
    }

    case 'SET_DATE': {
      return {
        ...state,
        cursor: null,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      };
    }

    case 'SET_SIMILAR_TO': {
      return {
        ...state,
        cursor: null,
        examples: action.payload.examples,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      };
    }

    case 'SET_COMMENTS_TO': {
      return {
        ...state,
        cursor: null,
        parentCompositeId: action.payload.parentCompositeId,
        isRepostsMode: false,
      };
    }

    case 'SET_REPOSTS_TO': {
      return {
        ...state,
        cursor: null,
        parentCompositeId: action.payload.parentCompositeId,
        isRepostsMode: !!action.payload.parentCompositeId,
      };
    }

    case 'SET_EXAMPLES': {
      return {
        ...state,
        examples: action.payload.examples,
      };
    }

    case 'SORT_BY': {
      return {
        ...state,
        cursor: null,
        sorting: { ...state.sorting, fieldName: action.fieldName },
      };
    }

    case 'IS_ASCENDING': {
      return {
        ...state,
        cursor: null,
        sorting: { ...state.sorting, isAscending: action.isAscending },
      };
    }

    case 'SET_CURSOR':
      return {
        ...state,
        cursor: action.cursor,
      };

    // Draft filter actions
    case 'UPDATE_DRAFT_FILTERS': {
      return {
        ...state,
        draftFilters: {
          ...state.draftFilters,
          ...action.payload,
        },
      };
    }

    case 'APPLY_DRAFT_FILTERS': {
      return {
        ...state,
        filters: { ...state.draftFilters },
        cursor: null, // Reset cursor when applying new filters
      };
    }

    case 'RESET_DRAFT_FILTERS': {
      return {
        ...state,
        draftFilters: { ...state.filters },
      };
    }

    case 'SYNC_FILTERS_TO_DRAFT': {
      return {
        ...state,
        draftFilters: { ...state.filters },
      };
    }

    default:
      return state;
  }
};